// Entry point. Factory-owned.
//
// Mounts the app and starts the chassis. Everything here runs once, in order,
// and the order matters: styles before mount so the first paint is not
// unstyled, mount before initPWA so a failed service-worker registration
// cannot stop the app rendering.

import { createApp } from 'vue';
import { IonicVue } from '@ionic/vue';

// Ionic's own stylesheets, then the palette, then the mapping, then yours.
// The order is the contract:
//
//   core/normalize/structure/typography  Ionic's reset and layout primitives
//   tokens.css                           this app's palette, both schemes
//   ionic.css                            maps every Ionic variable onto it
//   app.css                              yours
//   recipe.css                           the design recipe's overlay of the scale
//
// recipe.css is imported unconditionally and ships empty, so the load order is
// a fact of the chassis rather than something the design adapter has to edit
// into this file — which matters, because this file is factory-owned and an
// upgrade would take that edit straight back out. It carries nothing but
// `:root` custom properties, so any real rule in app.css still beats it.
//
// Ionic's own dark palette, applied by class rather than by the OS media
// query: the app has a Light / Dark / System setting, and a pinned choice is
// exactly the case where the OS is wrong. src/theme.js toggles
// `ion-palette-dark` on <html> alongside the app's own `data-scheme`.
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
// Ionic's optional utility classes — ion-padding, ion-text-center and friends.
// The pages use them instead of bespoke layout CSS, so they are not optional here.
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/palettes/dark.class.css';
import './styles/ionic.css';
import './styles/app.css';
import './styles/recipe.css';

import router, { HOME } from './router.js';
import App from './App.vue';
import { initPWA } from './core/pwa.js';
import { setState } from './core/state.js';
import { app } from './core/app.js';
import { bus } from './core/state.js';
import { externalizeSiteLinks, onAppUrlOpen } from './core/native.js';
import * as liveUpdate from './core/liveupdate.js';
import { reloadsHeld } from './reload-guard.js';
import { startSnapshotWrites } from './snapshot-bridge.js';
// Enabled addons, if any. An empty registry pulls in nothing, which is what
// makes an unused addon genuinely free rather than merely inert.
import addons from './addons.js';
import { initTheme } from './theme.js';
import { billing } from './billing.js';
import { currentCity } from './city.js';
import { bootNetwork } from './network-data.js';
import { openRoute } from './deep-link.js';
import { openArrival } from './arrival-open.js';
import { attribution } from './attribution.js';
import { reviewPrompt } from './review-prompt.js';

// Ionic needs the plugin AND a router: `@ionic/vue` imports vue-router at
// module load, so there is no version of adopting Ionic that keeps a
// URL-less navigation scheme of its own.
initTheme();

const vueApp = createApp(App).use(IonicVue).use(router);

// A memory history starts at NO location at all, and `isReady()` waits for an
// initial navigation that will never happen on its own. Without this push the
// promise never settles, mount is never called, and the page stays blank with
// nothing in the console to explain it. Web history does this for you from the
// URL, which is why the trap only bites the mode that has no URL — and this
// app is a native shell and a PWA served from /app and /demo, so a URL-driven
// router would fight both the server's surface split and the deep-link handler
// below.
router.push(HOME);
// The city in force has to be in memory before anything reads it: the network
// readers are synchronous (src/network.js) and its bundle is a chunk of its own.
// Awaited with the router, so both fetches run together. It is retried once, and
// then in the background (Home says so while it is down); the app still mounts
// rather than leaving a blank screen. The deep link, the arrival notice and the
// first widget snapshot all wait on this same promise, because each of them
// looks a station up.
const networkReady = bootNetwork(currentCity());
Promise.all([router.isReady(), networkReady])
  .then(() => vueApp.mount('#app'));

// A way in, for QA only.
//
// This app navigates through a MEMORY history, so every URL the server serves
// renders the same initial screen. That is correct for a native shell and a
// PWA served at /app and /demo — and it silently defeated the screenshot pass:
// QA navigated to /tabs/items, got a 200, photographed Home, and reported that
// it had audited every screen. Five screens, five identical pictures, a green
// tick. Worse than no audit, because it reads as one.
//
// Gated on the flag the QA fixture sets before any app code runs, so nothing
// is exposed in a real build.
if (typeof window !== 'undefined' && window.__APPFACTORY_QA__) {
  window.__appfactoryQa = {
    navigate: (path) => router.push(path),
    route: () => router.currentRoute.value?.fullPath ?? null,

    // BY NAME as well, because a path is not stable and a name is.
    //
    // QA asks for a screen using the route the PLAN states — `/`, `/tasks/:id`.
    // The navigation shell decides where those actually mount, and bottom-tabs
    // nests all of them under /tabs/. So QA pushed `/`, the app arrived
    // correctly at `/tabs/`, and QA compared the two strings, saw a mismatch
    // and reported the primary screen as unreachable — on an app where
    // navigation worked perfectly.
    //
    // Every product route is registered with its plan id as `name`, so this is
    // the same question asked in a form no shell can rewrite.
    screen: (id) => router.push({ name: id }),
    id: () => {
      const n = router.currentRoute.value?.name;
      return typeof n === 'string' ? n : null;
    },
  };
}

setState({ ready: true });

// The subscription, asked once at launch: whether this build can sell at all
// (the Billing plugin), and whether this phone has paid. A lapse since the
// last open is said once, here. Nothing else about it shows unasked.
billing.start().then(() => billing.showLapseIfDue()).catch(() => {});

// Apple Ads attribution: once, on the first launch after an iOS install, and
// nothing at all anywhere else. See src/attribution.js.
attribution.start().catch(() => {});

// The install clock for the rating prompt (src/review-prompt.js).
reviewPrompt.noteLaunch();

// After mount, and deliberately not awaited. A service worker that fails to
// register, or an OTA check that cannot reach the server, must not leave the
// user looking at an empty screen — which is exactly what happened when the
// old chassis ran its boot sequence before rendering anything.
initPWA();
externalizeSiteLinks();
liveUpdate.start({
  // Not while a permission prompt is up or just answered, and not during the
  // welcome: an update that reloads the WebView there throws away what the
  // person has typed and tapped, and reads as the app restarting itself.
  isSafeToReload: () => !reloadsHeld() && router.currentRoute.value?.name !== 'onboarding',
});

// Addons receive the host, exactly as they did on the 0.1 chassis — several of
// them extend it through `app.constructor.prototype`, and a plugin contract
// that changes with the renderer is not a contract.
for (const addon of addons) {
  // One addon that throws must not take the rest of them, or the app, with it.
  try { addon.boot?.(app); } catch (e) { console.error(`addon:${addon.id}`, e); }
}

// Deep links, from a cold start and from the native appUrlOpen event.
//
// A tapped widget arrives here, as `lateagain://station/1034?city=melbourne`
// or as the https twin of it — see `src/widget-link.js` for the whole of the
// scheme. Nothing else in the app listens on the `route` bus yet, so the
// handler is installed here rather than in a page that may not be mounted when
// a cold start delivers the URL.
bus.on('route', (url) => openRoute(url, { router, ready: networkReady }));
onAppUrlOpen((url) => app.route(url));
app.route(location.href);

// "You're at the station" (opt-in, iOS): opening the app soon after that notice
// lands on the board for the station, with the other one as the destination.
// Also keeps the watched places in step with home and work.
const openArrivalNow = () => openArrival({ router, ready: networkReady });
openArrivalNow();
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') openArrivalNow();
});

// The shared container the widget, and anything else outside the WebView,
// reads. A no-op on the web.
startSnapshotWrites(networkReady);

// Boot finished without throwing.
//
// Set LAST, deliberately: it is the one signal that distinguishes "the page
// rendered" from "everything the chassis does actually ran". The smoke suite
// waits on it, and it is worth having outside the tests too — it is the
// difference between a blank screen you have to debug and a blank screen that
// tells you boot never completed.
document.documentElement.dataset.ready = '1';
